import {
    useMutation,
    UseMutationOptions,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { createDownloadBlob, useAccessToken, useStandardErrorHandler } from "modules/common";
import { toast } from "react-toastify";
import {
    getRecords,
    createRecord,
    GetRecordRequest,
    CreateRecordRequest,
    editRecord,
    EditRecordRequest,
    deleteRecord,
    RecordCategory,
    CreateRecordCategoryRequest,
    createRecordCategory,
    EditRecordCategoryRequest,
    editRecordCategory,
    getRecordCategories,
    downloadRecordFile,
    bulkEditRecords,
    Record,
    deleteRecordCategory,
    bulkDeleteRecords,
    getRecord,
} from "services/records";

export const useGetRecords = (data: GetRecordRequest, enabled = true) => {
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju vanjskih modula");
    const accessToken = useAccessToken();

    const queryFn = () => getRecords(data, accessToken!);

    return useQuery({
        queryKey: ["records", data],
        queryFn,
        onError: errorHandler,
        keepPreviousData: true,
        enabled: Boolean(data.recordCategoryGuid),
    });
};

export const useGetRecord = (recordId: string, enabled = true) => {
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju vanjskih modula");
    const accessToken = useAccessToken();

    return useQuery({
        queryKey: ["recordDetails", recordId],
        queryFn: () => getRecord(recordId, accessToken!),
        onError: errorHandler,
        enabled: enabled,
    });
};

export const useGetRecordCategoriesAsItems = () => {
    const { data: recordCategoriesData } = useGetRecordCategories();

    return recordCategoriesData?.map(category => ({
        label: category.name,
        id: category.guid,
    }));
};

export const useCreateRecord = (
    uploadedFile: File | null,
    options?: UseMutationOptions<Record, unknown, CreateRecordRequest>
) => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler("Greška pri stvaranju evidencije");

    return useMutation({
        mutationFn: (data: CreateRecordRequest) => createRecord(data, uploadedFile, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["records"]);
            queryClient.invalidateQueries(["recordCategories"]);
            toast.success("Evidencija je uspješno stvorena");
        },
        ...options,
    });
};

export const useEditRecord = (uploadedFile: File | null) => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler("Greška pri uređivanju evidencije");

    return useMutation({
        mutationFn: (data: EditRecordRequest) => editRecord(data, uploadedFile, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["records"]);
            queryClient.invalidateQueries(["recordCategories"]);
            toast.success("Evidencija je uspješno uređena");
        },
    });
};

export const useBulkEditRecord = (recordGuids: string[]) => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler("Greška pri grupnom uređivanju evidencije");

    return useMutation({
        mutationFn: (data: EditRecordRequest) => bulkEditRecords(data, recordGuids, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["records"]);
            queryClient.invalidateQueries(["recordCategories"]);
            toast.success("Evidencije su uspješno uređene");
        },
    });
};

export const useDeleteRecord = () => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler("Greška pri brisanju evidencije");

    return useMutation({
        mutationFn: (id: string) => deleteRecord(id, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["records"]);
            toast.success("Evidencija je uspješno obrisana");
        },
    });
};

export const useBulkDeleteRecords = () => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler("Greška pri grupnom brisanju evidencija");

    return useMutation({
        mutationFn: (recordGuids: string[]) => bulkDeleteRecords(recordGuids, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["records"]);
            queryClient.invalidateQueries(["recordCategories"]);
            toast.success("Evidencije su uspješno obrisane");
        },
    });
};

export const useGetRecordCategories = (options: UseQueryOptions<RecordCategory[]> = {}) => {
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju kategorija evidencije");
    const accessToken = useAccessToken();
    const { enabled, ...opts } = options;

    const queryFn = () => getRecordCategories(accessToken!);

    return useQuery({
        queryKey: ["recordCategories"],
        queryFn,
        onError: errorHandler,
        keepPreviousData: true,
        ...opts,
    });
};

export const useCreateRecordCategory = (
    options?: UseMutationOptions<RecordCategory, unknown, CreateRecordCategoryRequest>
) => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler("Greška pri stvaranju katogorije evidencije");

    return useMutation({
        mutationFn: (data: CreateRecordCategoryRequest) => createRecordCategory(data, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["recordCategories"]);
            toast.success("Kategorija evidencije je uspješno stvorena");
        },
        ...options,
    });
};

export const useEditRecordCategory = (
    options?: UseMutationOptions<RecordCategory, unknown, EditRecordCategoryRequest>
) => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler("Greška pri uređivanju kategorije evidencije");

    return useMutation({
        mutationFn: (data: EditRecordCategoryRequest) => editRecordCategory(data, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["recordCategories"]);
            toast.success("Kategorija evidencije je uspješno uređena");
        },
        ...options,
    });
};

export const useDeleteRecordCategory = (options?: UseMutationOptions<unknown, unknown, string>) => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler("Greška pri brisanju kategorije evidencije");

    return useMutation({
        mutationFn: (id: string) => deleteRecordCategory(id, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["recordCategories"]);
            toast.success("Kategorija evidencije je uspješno obrisana");
        },
        ...options,
    });
};

export const downloadFile = async (recordGuid: string, accessToken: string, fileName: string) => {
    const response = await downloadRecordFile(recordGuid, accessToken);
    createDownloadBlob(response, fileName);
};
